import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { downloadXlsx } from '../utils/exportXlsx';
import { downloadStatsReportPdf, monthLabel, formatValue } from '../utils/statsReportPdf';

// Custom report builder. Pick a date range, tick the departments and attributes
// you want, optionally narrow to specific people, and get one month-by-month
// table per metric — on screen, and as Excel or PDF.
//
// The metric catalog comes from the server (/reports/metrics) so there is one
// definition of what we can report on; this file only lays it out. Admin-only,
// like the rest of the report surfaces.

const ymOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// n months back from the current month, as 'YYYY-MM'
function shiftYm(n) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return ymOf(d);
}

const firstDay = (ym) => `${ym}-01`;
const lastDay = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
};

const PRESETS = [
  { label: 'Last 3 months', from: () => shiftYm(-2) },
  { label: 'Last 6 months', from: () => shiftYm(-5) },
  { label: 'Last 12 months', from: () => shiftYm(-11) },
  { label: 'This year', from: () => `${new Date().getFullYear()}-01` },
];

// One metric's rows folded into a name × month grid, plus range and column
// totals. Counts and day-sums add up; averages are re-weighted by the number of
// records behind each cell, so a month with 2 jobs can't outvote one with 50.
function pivot(series, months) {
  const weighted = series.agg === 'avg';
  const byName = new Map();

  for (const r of series.rows) {
    let row = byName.get(r.person);
    if (!row) { row = { name: r.person, cells: {}, sum: 0, weight: 0 }; byName.set(r.person, row); }
    row.cells[r.month] = r.value;
    row.sum += weighted ? r.value * r.n : r.value;
    row.weight += r.n;
  }

  const names = [...byName.values()]
    .map((row) => ({ ...row, total: weighted ? (row.weight ? row.sum / row.weight : null) : row.sum }))
    .sort((a, b) => (b.total ?? -1) - (a.total ?? -1) || a.name.localeCompare(b.name));

  const colTotal = {};
  for (const mo of months) {
    let sum = 0, weight = 0, any = false;
    for (const r of series.rows) {
      if (r.month !== mo) continue;
      any = true;
      sum += weighted ? r.value * r.n : r.value;
      weight += r.n;
    }
    colTotal[mo] = !any ? null : weighted ? (weight ? sum / weight : null) : sum;
  }

  const totalSum = names.reduce((s, r) => s + r.sum, 0);
  const totalWeight = names.reduce((s, r) => s + r.weight, 0);
  const grand = !names.length ? null : weighted ? (totalWeight ? totalSum / totalWeight : null) : totalSum;

  return { series, names, colTotal, grand };
}

export default function ReportBuilderModal({ onClose }) {
  const [catalog, setCatalog] = useState(null);
  const [people, setPeople] = useState([]);
  const [loadError, setLoadError] = useState('');

  const [fromYm, setFromYm] = useState(() => shiftYm(-5));
  const [toYm, setToYm] = useState(() => shiftYm(0));
  const [picked, setPicked] = useState(() => new Set());
  const [pickedPeople, setPickedPeople] = useState([]);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState('');

  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState('');

  useEffect(() => {
    Promise.all([api.get('/reports/metrics'), api.get('/reports/people')])
      .then(([cat, ppl]) => { setCatalog(cat.data); setPeople(ppl.data || []); })
      .catch(() => setLoadError("Couldn't load the report options. Try again."));
  }, []);

  const byDept = useMemo(() => {
    if (!catalog) return [];
    return catalog.depts
      .map((d) => ({ ...d, metrics: catalog.metrics.filter((m) => m.dept === d.id) }))
      .filter((d) => d.metrics.length);
  }, [catalog]);

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const setDept = (dept, on) => setPicked((prev) => {
    const next = new Set(prev);
    for (const m of dept.metrics) { if (on) next.add(m.id); else next.delete(m.id); }
    return next;
  });

  const togglePerson = (name) =>
    setPickedPeople((prev) => (prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]));

  const rangeInvalid = fromYm > toYm;

  async function run() {
    setRunning(true);
    setRunError('');
    try {
      const { data } = await api.post('/reports/run', {
        from: firstDay(fromYm),
        to: lastDay(toYm),
        metrics: [...picked],
        people: pickedPeople,
      });
      setReport(data);
    } catch (err) {
      setRunError(err?.response?.data?.error || "Couldn't generate the report. Try again.");
    } finally {
      setRunning(false);
    }
  }

  const pivoted = useMemo(
    () => (report ? report.series.map((s) => pivot(s, report.months)) : []),
    [report]
  );

  function exportExcel() {
    const headers = ['Department', 'Metric', 'Breakdown', 'Name', ...report.months.map(monthLabel), 'Total'];
    const rows = [];
    for (const { series, names, colTotal, grand } of pivoted) {
      for (const r of names) {
        rows.push([
          series.deptLabel, series.label, series.groupLabel, r.name,
          ...report.months.map((mo) => formatValue(r.cells[mo] ?? null, series.agg)),
          formatValue(r.total, series.agg),
        ]);
      }
      if (names.length) {
        rows.push([
          series.deptLabel, series.label, series.groupLabel, series.agg === 'avg' ? 'All (weighted)' : 'All',
          ...report.months.map((mo) => formatValue(colTotal[mo], series.agg)),
          formatValue(grand, series.agg),
        ]);
      }
    }
    downloadXlsx({
      filename: `blvd-activity-report-${report.from}-to-${report.to}.xlsx`,
      sheetName: 'Report',
      headers,
      rows,
    });
  }

  function exportPdf() {
    downloadStatsReportPdf({
      report,
      pivoted,
      generatedAt: new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    });
  }

  const filteredPeople = people.filter((p) => p.toLowerCase().includes(peopleSearch.toLowerCase()));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
    >
      <div
        className="border rounded-lg w-full max-w-5xl max-h-[88vh] flex flex-col"
        style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b" style={{ borderColor: '#1e3a5f' }}>
          <div>
            <h2 className="text-lg font-black text-white tracking-widest uppercase">Generate Report</h2>
            <p className="text-xs text-gray-500 mt-1">
              {report
                ? `${report.series.length} metric${report.series.length === 1 ? '' : 's'} · ${report.months.length} month${report.months.length === 1 ? '' : 's'}${report.people.length ? ` · ${report.people.join(', ')}` : ''}`
                : 'Pick a range, then the departments and attributes you want'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none px-2" title="Close">✕</button>
        </div>

        {report ? (
          <ReportResults
            report={report}
            pivoted={pivoted}
            onBack={() => setReport(null)}
            onExcel={exportExcel}
            onPdf={exportPdf}
          />
        ) : (
          <>
            <div className="overflow-auto px-5 py-4 space-y-6">
              {loadError && <p className="text-red-400 text-sm">{loadError}</p>}
              {!catalog && !loadError && <p className="text-gray-500 text-sm">Loading options…</p>}

              {catalog && <>
                {/* Range */}
                <section>
                  <SectionLabel>Date range</SectionLabel>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {PRESETS.map((p) => {
                      const active = fromYm === p.from() && toYm === shiftYm(0);
                      return (
                        <button
                          key={p.label}
                          onClick={() => { setFromYm(p.from()); setToYm(shiftYm(0)); }}
                          className="text-xs font-bold uppercase tracking-wide px-2.5 py-1.5 rounded border transition"
                          style={active
                            ? { backgroundColor: '#c9a84c', color: '#0d1b2e', borderColor: '#c9a84c' }
                            : { borderColor: '#1e3a5f', color: '#9ca3af' }}
                        >{p.label}</button>
                      );
                    })}
                    <span className="text-xs text-gray-600 mx-1">or</span>
                    <MonthInput value={fromYm} onChange={setFromYm} max={toYm} />
                    <span className="text-xs text-gray-500">to</span>
                    <MonthInput value={toYm} onChange={setToYm} min={fromYm} />
                  </div>
                  {rangeInvalid && <p className="text-red-400 text-xs mt-2">Start month must come before the end month.</p>}
                </section>

                {/* People */}
                <section>
                  <div className="flex items-center gap-3">
                    <SectionLabel>People</SectionLabel>
                    <button
                      onClick={() => setPeopleOpen(!peopleOpen)}
                      className="text-xs text-gray-400 hover:text-white underline"
                    >{peopleOpen ? 'Hide' : 'Choose'}</button>
                    {pickedPeople.length > 0 && (
                      <button onClick={() => setPickedPeople([])} className="text-xs text-gray-500 hover:text-white">Clear</button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {pickedPeople.length
                      ? `Limited to ${pickedPeople.join(', ')}. Metrics grouped by client, category or program keep their full breakdown.`
                      : 'Everyone. Pick names to narrow the person-based metrics.'}
                  </p>
                  {peopleOpen && (
                    <div className="mt-2 border rounded p-3" style={{ borderColor: '#1e3a5f' }}>
                      <input
                        value={peopleSearch}
                        onChange={(e) => setPeopleSearch(e.target.value)}
                        placeholder="Search names…"
                        className="w-full text-xs rounded px-2 py-1.5 mb-2 outline-none"
                        style={{ backgroundColor: '#0a1524', border: '1px solid #1e3a5f', color: '#e5e7eb' }}
                      />
                      <div className="grid grid-cols-3 gap-x-4 gap-y-1 max-h-40 overflow-auto">
                        {filteredPeople.map((p) => (
                          <CheckRow key={p} checked={pickedPeople.includes(p)} onChange={() => togglePerson(p)} label={p} />
                        ))}
                        {!filteredPeople.length && <p className="text-xs text-gray-600">No matches.</p>}
                      </div>
                    </div>
                  )}
                </section>

                {/* Departments & attributes */}
                <section>
                  <SectionLabel>Departments &amp; attributes</SectionLabel>
                  <div className="mt-2 space-y-4">
                    {byDept.map((dept) => {
                      const on = dept.metrics.filter((m) => picked.has(m.id)).length;
                      return (
                        <div key={dept.id} className="border rounded p-3" style={{ borderColor: '#1e3a5f' }}>
                          <div className="flex items-center justify-between">
                            <span
                              className="inline-block text-xs font-black uppercase tracking-[0.15em] rounded px-2 py-0.5"
                              style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
                            >{dept.label}</span>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="text-gray-600">{on}/{dept.metrics.length}</span>
                              <button onClick={() => setDept(dept, true)} className="text-gray-400 hover:text-white">All</button>
                              <button onClick={() => setDept(dept, false)} className="text-gray-400 hover:text-white">None</button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-x-5 gap-y-2 mt-3">
                            {dept.metrics.map((m) => (
                              <label key={m.id} className="flex items-start gap-2 cursor-pointer group">
                                <input
                                  type="checkbox"
                                  aria-label={m.label}
                                  checked={picked.has(m.id)}
                                  onChange={() => toggle(m.id)}
                                  className="mt-0.5 accent-current"
                                  style={{ accentColor: '#c9a84c' }}
                                />
                                <span className="min-w-0">
                                  <span className="block text-xs text-gray-200 group-hover:text-white">
                                    {m.label}
                                    {m.groupLabel !== 'Person' && (
                                      <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wide ml-1.5">
                                        by {m.groupLabel}
                                      </span>
                                    )}
                                  </span>
                                  <span className="block text-[11px] text-gray-500 leading-snug">{m.help}</span>
                                  {m.note && <span className="block text-[11px] text-yellow-500/80 leading-snug">{m.note}</span>}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </>}
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 p-5 border-t" style={{ borderColor: '#1e3a5f' }}>
              <p className="text-xs text-gray-500">
                {picked.size} attribute{picked.size === 1 ? '' : 's'} selected
              </p>
              {runError && <p className="text-red-400 text-xs">{runError}</p>}
              <button
                onClick={run}
                disabled={!picked.size || running || rangeInvalid}
                className="ml-auto text-xs px-4 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-40"
                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
              >{running ? 'Generating…' : 'Generate Report'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReportResults({ report, pivoted, onBack, onExcel, onPdf }) {
  return (
    <>
      <div className="overflow-auto px-5 py-4 space-y-7">
        {pivoted.map(({ series, names, colTotal, grand }) => (
          <section key={series.id}>
            <h3 className="text-sm font-black text-white uppercase tracking-widest">
              <span style={{ color: '#c9a84c' }}>{series.deptLabel}</span> — {series.label}
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">{series.help}</p>
            {series.note && <p className="text-[11px] text-yellow-500/80">{series.note}</p>}

            {!names.length ? (
              <p className="text-gray-500 text-sm mt-3">No activity in this range.</p>
            ) : (
              <div className="overflow-x-auto mt-3">
                <table className="text-xs border-collapse w-full">
                  <thead>
                    <tr>
                      <th className="text-left text-gray-400 font-bold uppercase tracking-wider pr-3 py-2">{series.groupLabel}</th>
                      {report.months.map((mo) => (
                        <th key={mo} className="px-2 py-2 text-center text-gray-400 font-normal whitespace-nowrap">{monthLabel(mo)}</th>
                      ))}
                      <th className="pl-3 py-2 text-right text-gray-400 font-bold uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {names.map((r) => (
                      <tr key={r.name} className="border-t" style={{ borderColor: '#1e3a5f' }}>
                        <td className="text-gray-300 font-bold pr-3 py-1.5 max-w-[16rem] truncate" title={r.name}>{r.name}</td>
                        {report.months.map((mo) => {
                          const v = r.cells[mo];
                          return (
                            <td key={mo} className="px-2 py-1.5 text-center">
                              {v == null ? <span className="text-gray-700">·</span> : (
                                <span
                                  className="inline-block min-w-[1.75rem] rounded px-1 py-0.5 font-bold"
                                  style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#c9a84c' }}
                                >{formatValue(v, series.agg)}</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="pl-3 py-1.5 text-right text-white font-bold">{formatValue(r.total, series.agg)}</td>
                      </tr>
                    ))}
                    <tr className="border-t" style={{ borderColor: '#1e3a5f' }}>
                      <td className="text-gray-500 uppercase font-bold pr-3 py-1.5">
                        {series.agg === 'avg' ? 'All (weighted)' : 'All'}
                      </td>
                      {report.months.map((mo) => (
                        <td key={mo} className="px-2 py-1.5 text-center text-gray-400 font-bold">
                          {colTotal[mo] == null ? '' : formatValue(colTotal[mo], series.agg)}
                        </td>
                      ))}
                      <td className="pl-3 py-1.5 text-right text-white font-bold">{formatValue(grand, series.agg)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}
      </div>

      <div className="flex items-center gap-3 p-5 border-t" style={{ borderColor: '#1e3a5f' }}>
        <button onClick={onBack} className="text-xs font-bold uppercase tracking-wide text-gray-400 hover:text-white">
          ‹ Change options
        </button>
        <div className="ml-auto flex gap-2">
          <button
            onClick={onPdf}
            className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide border transition hover:text-white"
            style={{ borderColor: '#c9a84c', color: '#c9a84c' }}
          >Download PDF</button>
          <button
            onClick={onExcel}
            className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >Export to Excel</button>
        </div>
      </div>
    </>
  );
}

function SectionLabel({ children }) {
  return <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{children}</span>;
}

function MonthInput({ value, onChange, min, max }) {
  return (
    <input
      type="month"
      value={value}
      min={min}
      max={max}
      onChange={(e) => e.target.value && onChange(e.target.value)}
      className="text-xs rounded px-2 py-1.5 outline-none"
      style={{ backgroundColor: '#0a1524', border: '1px solid #1e3a5f', color: '#e5e7eb', colorScheme: 'dark' }}
    />
  );
}

function CheckRow({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" aria-label={label} checked={checked} onChange={onChange} style={{ accentColor: '#c9a84c' }} />
      <span className="text-xs text-gray-300 truncate" title={label}>{label}</span>
    </label>
  );
}
